import { useMemo, useState } from "react";

import styles from "./Board.module.css";
import { BoardView } from "./BoardView";
import { useDocumentTitle } from "../../chrome/useDocumentTitle";
import { EmptyState } from "../../components/EmptyState";
import { Input } from "../../components/Input";
import { RepoAvatar } from "../../components/RepoAvatar";
import { Segmented } from "../../components/Segmented";
import { useCurrentWorkspace } from "../../data/api/currentWorkspace";
import { usePreferences } from "../../data/api/preferences";
import { useActivityTasks } from "../../data/api/tasks";
import { useRealtimeScope } from "../../data/realtime/useRealtimeScope";
import { Icon } from "../../icons";
import { paths } from "../../routing/paths";
import { useNav } from "../../routing/useNav";

const noop = (): void => undefined;

// Workspace-scope kinds that can add, move or retire a card on this board.
const BOARD_EVENTS: ReadonlySet<string> = new Set([
  "resync",
  "task.archived",
  "task.created",
  "task.updated",
]);

export const ActivityView = () => {
  const nav = useNav();
  const { preferences, setLayout } = usePreferences();
  const {
    error,
    loading,
    refetch,
    repoNames,
    tasks: allTasks,
  } = useActivityTasks();
  const { workspaceId } = useCurrentWorkspace();
  const [filter, setFilter] = useState("");
  useDocumentTitle("Activity");

  // The one scope this board can listen on: it spans every repository in the
  // workspace, so there is no `repo:` subscription to make. `task.created`
  // brings a new agent in the moment it is launched, from this tab or another;
  // `task.updated` moves its card, and the server sends that one here only for
  // the changes that move a task between columns (realtime.md §Event kinds), so
  // an agent's per-message chatter does not become a refetch per message.
  //
  // Filtered rather than refetching on anything: the workspace scope also
  // carries connection, repository and runner events this board does not
  // render. `resync` means the socket dropped and events may have been missed.
  const revoked = useRealtimeScope(
    workspaceId ? `workspace:${workspaceId}` : null,
    (event) => {
      if (BOARD_EVENTS.has(event.kind)) {
        refetch();
      }
    },
  );

  const repoNameFor = (id: string): string => repoNames.get(id) ?? id;

  const tasks = useMemo(() => {
    if (!filter) {
      return allTasks;
    }
    const q = filter.toLowerCase();
    return allTasks.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.displayId.toLowerCase().includes(q) ||
        (repoNames.get(t.repo) ?? "").toLowerCase().includes(q),
    );
  }, [allTasks, filter, repoNames]);

  return (
    <>
      <div className={styles.toolbar}>
        <div className={styles.toolbarHead}>
          <RepoAvatar
            org="A"
            style={{
              background: "var(--accent-tint)",
              borderColor: "var(--accent-3)",
              color: "var(--accent-2)",
            }}
          >
            <Icon.Kanban size={16} />
          </RepoAvatar>
          <div>
            <div className={styles.toolbarName}>Activity</div>
          </div>
        </div>
        <div className={styles.grow} />
        <Input
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter across repos…"
          style={{ width: 220 }}
          value={filter}
        />
        <Segmented
          onValueChange={(v) => setLayout(v as "kanban" | "list")}
          options={[
            {
              label: (
                <>
                  <Icon.Kanban size={12} /> Kanban
                </>
              ),
              value: "kanban",
            },
            {
              label: (
                <>
                  <Icon.List size={12} /> List
                </>
              ),
              value: "list",
            },
          ]}
          value={preferences.layout}
        />
      </div>

      {revoked ? (
        <EmptyState
          body="Your access to this workspace was removed. Reload if you think that's wrong."
          title="You no longer have access."
        />
      ) : loading && allTasks.length === 0 ? (
        <div className={styles.loading}>Loading activity…</div>
      ) : error ? (
        <EmptyState
          body="We couldn't reach the server to load activity."
          title="Something went sideways."
        />
      ) : tasks.length === 0 ? (
        <EmptyState
          body="When agents are working across your repositories, they'll show up here."
          title="No activity yet."
        />
      ) : (
        <BoardView
          archivedIds={[]}
          layout={preferences.layout}
          onArchive={noop}
          onArchiveTask={noop}
          onOpen={(t) => nav.task(t.repo, t.displayId)}
          repoNameFor={repoNameFor}
          taskHref={(t) => paths.task(t.repo, t.displayId)}
          tasks={tasks}
        />
      )}
    </>
  );
};
